/**
 * Client-side error reporter — single fire-and-forget path used by
 * AppErrorBoundary, DialerErrorBoundary, window.onerror, and the
 * unhandledrejection handler. Sends a structured payload to
 * /api/client-errors which forwards to the backend and emits Cloud Logging
 * `event:client_error`. The window/unhandledrejection paths also forward
 * to PostHog Error Tracking — boundary callers do that themselves so they
 * can attach section/feature context.
 *
 * Must NEVER throw — a logging failure can never be allowed to re-trigger
 * the boundary it's reporting on.
 */

import { captureException } from "@/lib/posthog";
import { shouldIgnoreError } from "@/lib/errorFilter";

type ErrorSource = "boundary" | "window" | "unhandledrejection";

interface ReportInput {
  error: unknown;
  /** Omitted by non-boundary callers (e.g. media playback). The backend's zod
   *  enum accepts only the three values above, and anything else downgrades the
   *  whole report to `client_error_malformed` — so leave it off rather than
   *  inventing a fourth. `feature`/`section` carry the discriminator instead. */
  source?: ErrorSource;
  section?: string;
  feature?: string;
  componentStack?: string;
}

let cachedUserId: string | null = null;

/**
 * Set or clear the userId attached to subsequent reports. Called from a
 * provider that subscribes to the NextAuth session — the reporter itself
 * is module-scoped so class components can use it without React context.
 */
export function setReporterUserId(userId: string | null | undefined) {
  cachedUserId = userId ?? null;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || String(error);
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function errorStack(error: unknown): string | undefined {
  if (error instanceof Error) return error.stack;
  return undefined;
}

/**
 * The page address without one-time tokens: an invitation link carries its token in the fragment
 * (`/invite/accept#token=…`) until the page strips it, and client_error lines are kept in Cloud Logging.
 */
function reportableUrl(href: string): string {
  return href.replace(/([#?&]token=)[^&#]*/gi, "$1[redacted]");
}

export function reportClientError(input: ReportInput): void {
  try {
    if (typeof fetch === "undefined") return;

    const body = {
      message: errorMessage(input.error).slice(0, 2000),
      stack: errorStack(input.error)?.slice(0, 10000),
      componentStack: input.componentStack?.slice(0, 10000),
      section: input.section,
      feature: input.feature,
      source: input.source,
      url: typeof window !== "undefined" ? reportableUrl(window.location.href) : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      userId: cachedUserId ?? undefined,
    };

    // keepalive lets the report finish even if the user navigates away
    // immediately after the crash. Errors here are swallowed; the reporter
    // must never become a source of new errors.
    fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Fail silent. A logging path that throws is worse than no logging.
  }
}

/**
 * Register window-level handlers so async errors and uncaught promise
 * rejections that bypass React's error boundary tree still reach Cloud
 * Logging. Returns a cleanup function for the React useEffect contract.
 */
export function registerGlobalHandlers(): () => void {
  if (typeof window === "undefined") return () => {};

  const onError = (event: ErrorEvent) => {
    if (shouldIgnoreError(event.message, event.filename, event.error?.name)) return;
    const err = event.error || new Error(event.message);
    reportClientError({ error: err, source: "window" });
    captureException(err, { source: "window", filename: event.filename });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const msg = errorMessage(reason);
    const name = reason instanceof Error ? reason.name : undefined;
    if (shouldIgnoreError(msg, undefined, name)) return;
    const err = reason instanceof Error ? reason : new Error(msg);
    reportClientError({ error: err, source: "unhandledrejection" });
    captureException(err, { source: "unhandledrejection" });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
